/**
 * Super-Agent follow-up suggestion chips.
 *
 * After the Super-Agent finishes a response, this component fetches AI-
 * generated next-step suggestions and renders them as a minimal list — a
 * small heading above plain rows separated by hairline borders (no card
 * chrome). Clicking a row sends the prompt immediately (handled by the
 * parent) — it does not stage the text in the composer first.
 *
 * No loading state is shown. The fetch very often resolves to an empty list
 * (the server's suppression rules, or the model declining to suggest), and a
 * skeleton that appears and then vanishes with nothing reads as a glitch.
 * Instead the chips simply fade in once they arrive, and nothing renders at
 * all while the (background) request is in flight or when it comes back empty.
 *
 * The server owns all scoping/suppression rules; this component only adds the
 * client-side guards that depend on UI state (mid-stream, chip-spiral).
 *
 * @module components/chat/followup-suggestions
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { DynamicIcon } from "lucide-react/dynamic";
import { chatService, type FollowupSuggestion } from "@/services/chat-service";
import { resolveChatIcon } from "@/lib/chat-icon";
import { getChromeStrings, useEmbedLocale } from "@/lib/embed-locale";
import { cn } from "@/lib/utils";

/**
 * Props for {@link FollowupSuggestions}.
 */
interface FollowupSuggestionsProps {
  /** Chat ID (client-generated UUID, also the DB primary key). */
  chatId: string;
  /** Current chat streaming status from `useChat`. */
  status: string;
  /** Whether follow-up chips are enabled for this chat — a Super-Agent chat, or
   * a direct agent whose `showFollowUp` capability is on. */
  enabled: boolean;
  /** True when the most recent user turn was sent from a chip (anti-spiral). */
  lastTurnFromChip: boolean;
  /** Called with the chip's full prompt; the parent sends it immediately. */
  onSelect: (fullPrompt: string) => void;
}

/** Wrapper width — matches the chat message column. */
const WRAPPER_CLASS =
  "mx-auto w-full max-w-full px-3 pb-3 lg:max-w-3xl lg:px-4 xl:max-w-4xl";

/**
 * Fetches and renders follow-up suggestion chips once the Super-Agent's
 * response settles.
 */
export function FollowupSuggestions({
  chatId,
  status,
  enabled,
  lastTurnFromChip,
  onSelect,
}: FollowupSuggestionsProps) {
  const [suggestions, setSuggestions] = useState<FollowupSuggestion[]>([]);
  // Locked embed session locale (null in the main app). Drives both the
  // localized "Suggested" heading and the language the server authors chips in
  // — so a language-locked chat gets chips in the locked language, not the
  // language the visitor happens to be typing in.
  const locale = useEmbedLocale();
  const chrome = getChromeStrings(locale);

  const prevStatusRef = useRef<string>(status);

  /** Fetches follow-up suggestions for the current turn. */
  const fetchSuggestions = useCallback(async () => {
    try {
      const response = await chatService.generateFollowups(chatId, locale);
      const next = response?.data?.suggestions;
      setSuggestions(Array.isArray(next) ? next : []);
    } catch {
      setSuggestions([]);
    }
  }, [chatId, locale]);

  /**
   * Detects the streaming → ready transition and, subject to the client-side
   * suppression rules, kicks off a suggestion fetch.
   */
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;

    // A new user turn started — clear the previous turn's chips.
    if (status === "submitted" || status === "streaming") {
      setSuggestions([]);
      return;
    }

    if (status !== "ready" || prev === "ready") return;

    // A turn just settled. Fetch chips for it — unless it was sent from a
    // chip (anti-spiral). When suppressed, clear any chips from a prior turn
    // so none linger. Server-side rules (short conversation, clarifying
    // question) make the fetch itself return an empty list.
    if (enabled && !lastTurnFromChip) {
      void fetchSuggestions();
    } else {
      setSuggestions([]);
    }
  }, [status, enabled, lastTurnFromChip, fetchSuggestions]);

  if (!enabled || status !== "ready" || suggestions.length === 0) return null;

  return (
    <div className={WRAPPER_CLASS} data-testid="followup-suggestions">
      {/* Small heading — no card chrome, just a label above the rows. */}
      <p
        className={cn(
          "px-2 pb-0.5 text-xs font-medium text-muted-foreground",
          "animate-in fade-in fill-mode-both duration-200 ease-out",
        )}
      >
        {chrome.suggested}
      </p>

      <div className="divide-y divide-border/60 border-t border-border/60">
        {suggestions.map((suggestion, index) => (
          <button
            key={`${suggestion.label}-${index}`}
            type="button"
            data-testid="followup-chip"
            title={suggestion.fullPrompt}
            onClick={() => onSelect(suggestion.fullPrompt)}
            // Staggered entrance — each chip fades + slides up just after the
            // one before it, so the strip resolves with a smooth cascade.
            style={{ animationDelay: `${80 + index * 70}ms` }}
            className={cn(
              "group flex w-full items-center gap-2.5 px-2 py-2.5 text-left",
              "text-muted-foreground transition-colors hover:text-foreground",
              "focus-visible:text-foreground focus-visible:outline-none",
              "animate-in fade-in slide-in-from-bottom-2 fill-mode-both",
              "duration-300 ease-out",
            )}
          >
            <DynamicIcon
              name={
                resolveChatIcon(suggestion.icon) as Parameters<
                  typeof DynamicIcon
                >[0]["name"]
              }
              className="h-4 w-4 shrink-0 opacity-70"
            />
            <span className="min-w-0 flex-1 truncate text-sm">
              {suggestion.label}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 opacity-40 transition-opacity group-hover:opacity-70" />
          </button>
        ))}
      </div>
    </div>
  );
}
